// Progress reporting API.
// Mirrors the axios+token pattern used by api/onboarding.ts
// (token in localStorage 'cleveroo_token', base from REACT_APP_API_URL).

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function authHeaders() {
  const token =
    localStorage.getItem('cleveroo_token') ||
    localStorage.getItem('accessToken') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type DomainStatus = 'strength' | 'needs_work' | 'steady';
export type Trend = 'improving' | 'steady' | 'declining' | 'none';

export interface ProgressViewer {
  audience: 'parent' | 'self';
  subjectName: string | null;
  possessive: string; // e.g. "Maya's", "your child's", "your"
}

export interface ProgressSummary {
  testsCompleted: number;
  questionsAnswered: number;
  questionsCorrect: number;
  overallAccuracy: number;
  trend: Trend;
  lastActivityAt: string | null;
}

export interface ProgressDomain {
  domain: string;
  label: string;
  subjects: string[];
  attempts: number;
  questionsAnswered: number;
  questionsCorrect: number;
  accuracy: number;
  averageScore: number;
  status: DomainStatus;
}

export interface ProgressPoint {
  index: number;
  attemptId: string;
  date: string;
  score: number;
  domain: string;
  label: string;
  testTitle: string | null;
}

/** One full assessment (placement/retake) from placement_attempts (ROO-166). */
export interface AssessmentPoint {
  id: number;
  scorePercent: number;
  band: number;
  at: string;
}

export interface WeeklyPracticePoint {
  weekStart: string;
  answered: number;
  correct: number;
}

/** Per-knowledge-point accuracy trend: recent 14 days vs before (ROO-166). */
export interface SkillTrend {
  skill: string;
  label: string;
  accuracy: number;
  recent: number | null;
  prior: number | null;
  total: number;
  trend: 'up' | 'down' | 'flat' | 'new' | 'idle';
}

export interface ProgressResponse {
  viewer: ProgressViewer;
  hasData: boolean;
  summary: ProgressSummary;
  domains: ProgressDomain[];
  strengths: string[];
  weaknesses: string[];
  timeSeries: ProgressPoint[];
  /** Assessment history + practice trends (ROO-166). */
  assessments: AssessmentPoint[];
  weeklyPractice: WeeklyPracticePoint[];
  skillTrends: SkillTrend[];
}

export async function getProgress(studentId: string): Promise<ProgressResponse | null> {
  const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/progress`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return res.data?.data ?? null;
}
